import { Component, OnInit, signal, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime } from 'rxjs';

import { PurchaseRequestService } from '../purchase-request.service';
import {
  PurchaseRequestRecord,
  PurchaseRequestPurpose,
  PurchaseRequestStatus,
} from '../purchase-request.config';
import { PurchaseRequestUpdateDTO } from '../../../../../api/purchase-service/model/purchaseRequestUpdateDTO';
import {
  AddFormComponent,
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { PurchaseRequestItem } from '../models/purchase-request-item';
import { PurchaseRequestItemDialogComponent } from '../../components/selected-sku-dialog/selected-sku-dialog.component';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-purchase-request-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    PurchaseRequestItemDialogComponent,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-request-edit.component.html',
  styleUrl: './purchase-request-edit.component.css',
})
export class PurchaseRequestEditComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('addForm') private readonly addFormComponent?: AddFormComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly translateService = inject(TranslateService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>([]);
  tableSections = signal<TableSection[]>([]);
  headerActions = signal<ActionButton[]>([]);

  // Data
  itemsList = signal<PurchaseRequestItem[]>([]);
  selectedItems = signal<PurchaseRequestItem[]>([]);
  deletedItems = signal<PurchaseRequestItem[]>([]);
  loading = signal(false);
  saving = signal(false);
  currentFormData: any = null;
  purchaseRequestData = signal<PurchaseRequestRecord | null>(null);
  warehouseOptions = signal<{ label: string; value: string }[]>([]);
  private skipCanDeactivate = false;
  private readonly showRequiredValidationErrors = signal(false);

  // Dialog states
  showItemDialog = signal(false);
  editingItem = signal<PurchaseRequestItem | null>(null);

  // Cell editing
  private readonly cellValueChange$ = new Subject<{
    rowData: any;
    prop: string;
    value: any;
  }>();

  // Constants
  readonly PurchaseRequestPurpose = PurchaseRequestPurpose;
  readonly PurchaseRequestStatus = PurchaseRequestStatus;

  ngOnInit(): void {
    const id = parsePositiveIntRouteParam(this.route.snapshot.paramMap.get('id'));
    if (id != null) {
      this.initializeConfigurations();
      this.loadOptions();
      this.loadWarehouses();
      this.loadPurchaseRequestData(id);
      // Initialize table data
      this.updateItemsTableData();

      // Set up debounced cell value changes to prevent input focus loss
      this.cellValueChange$
        .pipe(
          debounceTime(300), // Wait 300ms after user stops typing
          takeUntil(this.destroy$)
        )
        .subscribe((event) => {
          this.updateItemsListFromCellChange(event);
        });
    } else {
      this.router.navigate(['/procurement/purchase-request']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
      case 'createPurchaseOrder':
        this.onCreatePurchaseOrder();
        break;
      case 'editPurchaseOrder':
        this.onEditPurchaseOrder();
        break;
    }
  }

  onTableAction(event: { action: string; data?: any; section?: string }): void {
    switch (event.action) {
      case 'addItem':
        this.onAddItem();
        break;
      case 'editItem':
        this.onEditItem(event.data);
        break;
      case 'deleteItem':
        this.onDeleteItem(event.data);
        break;
      case 'deleteSelectedItems':
        this.onDeleteSelectedItems();
        break;
      case 'selectionChange':
        this.onSelectionChange(event.section, event.data);
        break;
      case 'cellEdit':
        this.onCellValueChange(event.data);
        break;
      case 'cellValueChange':
        this.onCellValueChange(event.data);
        break;
      default:
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    // Store the current form data
    this.currentFormData = formData;

    // Reinitialize header actions to show/hide "Create new purchase order" button based on status
    this.initializeHeaderActions();
  }

  private loadOptions(): void {
    // Load purpose and status options with translated labels
    this.formFields.set(
      this.formFields().map((field) => {
        if (field.controlName === 'purpose') {
          return { ...field, options: this.getPurposeOptions() };
        }
        if (field.controlName === 'status') {
          return { ...field, options: this.getStatusOptions() };
        }
        return field;
      })
    );
  }

  private getPurposeOptions(): any[] {
    return [
      {
        label: this.translateService.instant(
          'purchase_request.purpose.purchase'
        ),
        value: PurchaseRequestPurpose.PURCHASE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_transfer'
        ),
        value: PurchaseRequestPurpose.MATERIAL_TRANSFER,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_issue'
        ),
        value: PurchaseRequestPurpose.MATERIAL_ISSUE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.manufacture'
        ),
        value: PurchaseRequestPurpose.MANUFACTURE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.customer_provided'
        ),
        value: PurchaseRequestPurpose.CUSTOMER_PROVIDED,
      },
    ];
  }

  private getStatusOptions(): any[] {
    return [
      {
        label: this.translateService.instant('purchase_request.status.draft'),
        value: PurchaseRequestStatus.DRAFT,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.submitted'
        ),
        value: PurchaseRequestStatus.SUBMITTED,
      },
      {
        label: this.translateService.instant('purchase_request.status.stopped'),
        value: PurchaseRequestStatus.STOPPED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.cancelled'
        ),
        value: PurchaseRequestStatus.CANCELLED,
      },
      {
        label: this.translateService.instant('purchase_request.status.pending'),
        value: PurchaseRequestStatus.PENDING,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.partially_ordered'
        ),
        value: PurchaseRequestStatus.PARTIALLY_ORDERED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.partially_received'
        ),
        value: PurchaseRequestStatus.PARTIALLY_RECEIVED,
      },
      {
        label: this.translateService.instant('purchase_request.status.ordered'),
        value: PurchaseRequestStatus.ORDERED,
      },
      {
        label: this.translateService.instant('purchase_request.status.issued'),
        value: PurchaseRequestStatus.ISSUED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.transferred'
        ),
        value: PurchaseRequestStatus.TRANSFERRED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.received'
        ),
        value: PurchaseRequestStatus.RECEIVED,
      },
    ];
  }

  private loadPurchaseRequestData(id: number): void {
    this.loading.set(true);

    this.purchaseRequestService
      .getPurchaseRequestById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.purchaseRequestData.set(response);
            this.populateFormData(response);
            // Load items from API response like view page
            this.loadItemsFromApiResponse(response);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'purchase_request.messages.not_found'
              ),
            });
            this.router.navigate(['/procurement/purchase-request']);
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_load_data'
            ),
          });
          this.router.navigate(['/procurement/purchase-request']);
        },
      });
  }

  private populateFormData(data: PurchaseRequestRecord): void {
    // Set the current form data to be used by the add-form component
    this.currentFormData = {
      series: data.series || '',
      title: data.title,
      scheduleDate: this.parseDateToMidnight(data.scheduleDate),
      status: data.status || 'PENDING_APPROVAL',
    };

    // Update header actions after data is loaded
    this.initializeHeaderActions();
  }

  private parseDateToMidnight(dateString: string): Date {
    if (!dateString) return new Date();

    // Parse the date and set time to midnight (00:00:00) to avoid timezone issues
    const date = new Date(dateString);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private loadItemsFromApiResponse(data: PurchaseRequestRecord): void {
    // Use service method to map API items to PurchaseRequestItem format
    const mappedItems =
      this.purchaseRequestService.mapApiItemsToPurchaseRequestItems(
        data.items || []
      );

    // Remove duplicates based on ID to prevent issues
    const uniqueItems = this.removeDuplicateItems(mappedItems);

    // Add order field to each item for tracking position
    const itemsWithOrder = uniqueItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    this.itemsList.set(itemsWithOrder);
    this.updateItemsTableData();
  }

  private removeDuplicateItems(
    items: PurchaseRequestItem[]
  ): PurchaseRequestItem[] {
    const seen = new Set<string>();
    return items.filter((item) => {
      // Use ID for duplicate detection instead of SKU code
      // since different items can have the same SKU code
      const key = item.id;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private reorderItems(items: PurchaseRequestItem[]): PurchaseRequestItem[] {
    return items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
  }

  private loadMockItems(): void {
    // Mock items for demonstration - in real app, this would come from API
    const mockItems: PurchaseRequestItem[] = [
      {
        id: 'item_1',
        spuCode: 'SPU001',
        spuName: 'Sample Product 1',
        skuCode: 'SKU001',
        skuName: 'Sample SKU 1',
        quantity: 10,
      },
      {
        id: 'item_2',
        spuCode: 'SPU002',
        spuName: 'Sample Product 2',
        skuCode: 'SKU002',
        skuName: 'Sample SKU 2',
        quantity: 5,
      },
    ];
    this.itemsList.set(mockItems);
    this.updateItemsTableData();
  }

  private onSave(): void {
    this.showRequiredValidationErrors.set(true);
    this.updateItemsTableData();

    if (!this.currentFormData) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
      return;
    }

    if (this.hasMissingRequiredFormData()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
      return;
    }

    if (this.itemsList().length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_add_at_least_one_item'
        ),
      });
      return;
    }

    const validationErrors = this.validateItems();
    if (validationErrors.length > 0) {
      const messageKey = validationErrors.includes('missing_warehouse')
        ? 'purchase_request.messages.please_fill_required_fields'
        : 'purchase_request.messages.duplicate_sku_warehouse_combination';
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(messageKey),
      });
      return;
    }

    this.saving.set(true);

    // Create request item list for API using PurchaseRequestUpdateDTO structure
    // Include both current items and deleted items
    const currentItems = this.itemsList().map((item) => {
      // For existing items (with valid ID > 0), use the actual ID
      // For new items (ID = 0 or invalid), use 0
      const itemId = item.id && Number(item.id) > 0 ? Number(item.id) : 0;

      // Remove order field before submitting to BE
      const { order: _order } = item as any;

      return {
        id: itemId, // Use actual ID for existing items, 0 for new items
        skuCode: item.skuCode,
        scheduleDate: undefined, // Set to undefined since schedule date is removed from UI
        quantity: item.quantity,
        warehouseCode: item.warehouseCode || '',
        deleteFlag: false, // Required by PurchaseRequestItemUpdateDTO
      };
    });

    // Process deleted items
    const deletedItems = this.deletedItems().map((item) => {
      const itemId = item.id && Number(item.id) > 0 ? Number(item.id) : 0;

      // Remove order field before submitting to BE
      const { order: _order } = item as any;

      return {
        id: itemId,
        skuCode: item.skuCode,
        scheduleDate: undefined, // Set to undefined since schedule date is removed from UI
        quantity: item.quantity,
        warehouseCode: item.warehouseCode || '',
        deleteFlag: true, // Mark as deleted
      };
    });

    // Merge current items and deleted items
    const items = [...currentItems, ...deletedItems];

    const purchaseRequestData: PurchaseRequestUpdateDTO = {
      title: this.currentFormData.title,
      series: this.currentFormData.series,
      scheduleDate: this.currentFormData.scheduleDate,
      items: items,
    };

    // Get the ID from route params
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.saving.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.invalid_id'
        ),
      });
      return;
    }

    // Call the real API
    this.purchaseRequestService
      .updatePurchaseRequest(Number(id), purchaseRequestData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_request.messages.updated_successfully'
            ),
          });
          // Navigate back to the list page
          setTimeout(() => {
            this.router.navigate(['/procurement/purchase-request']);
          }, 1000);
        },
        error: (error) => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_update'
            ),
          });
        },
      });
  }

  private onCancel(): void {
    this.router.navigate(['/procurement/purchase-request']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving() || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private onCreatePurchaseOrder(): void {
    this.router.navigate(['/procurement/purchase-order/add'], {
      queryParams: { purchaseRequestId: this.purchaseRequestData()?.id },
    });
  }

  private onEditPurchaseOrder(): void {
    // Get the purchase order ID and navigate to edit page
    const purchaseOrderId = this.getPurchaseOrderId();
    if (purchaseOrderId) {
      this.router.navigate([
        '/procurement/purchase-order/edit',
        purchaseOrderId,
      ]);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.purchase_order_not_found'
        ),
      });
    }
  }

  private isStatusPending(): boolean {
    return this.currentFormData?.status === PurchaseRequestStatus.PENDING;
  }

  private isStatusApproved(): boolean {
    return this.currentFormData?.status === PurchaseRequestStatus.ORDERED;
  }

  private checkPurchaseOrderExists(): boolean {
    const purchaseRequestData = this.purchaseRequestData();
    return !!purchaseRequestData?.orders?.length;
  }

  private getPurchaseOrderId(): number | null {
    const purchaseRequestData = this.purchaseRequestData();
    if (purchaseRequestData?.orders?.length) {
      return purchaseRequestData.orders[0].id || null;
    }
    return null;
  }

  private initializeConfigurations(): void {
    // Initialize form fields with translated labels
    this.formFields.set([
      // Basic Information
      {
        label: this.translateService.instant('purchase_request.fields.series'),
        controlName: 'series',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_series'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 50,
      },
      {
        label: this.translateService.instant('purchase_request.fields.title'),
        controlName: 'title',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_title'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 200,
      },
      {
        label: this.translateService.instant(
          'purchase_request.fields.schedule_date'
        ),
        controlName: 'scheduleDate',
        type: 'date',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.select_schedule_date'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        minDate: new Date(), // Disable past dates
        disabled: this.isStatusApproved(), // Disable when status is APPROVED
      },
    ]);

    // Initialize table sections
    this.tableSections.set([
      {
        testIdKey: 'purchase_request.sections.item_details',
        title: this.translateService.instant(
          'purchase_request.sections.item_details'
        ),
        show: true,
        columns: [
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_code'
            ),
            prop: 'spuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_name'
            ),
            prop: 'spuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_code'
            ),
            prop: 'skuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_name'
            ),
            prop: 'skuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.quantity'
            ),
            prop: 'quantity',
            width: 120,
            type: this.isStatusApproved() ? 'text' : 'inputNumber',
            useGrouping: true,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.warehouse_code'
            ),
            prop: 'warehouseCode',
            width: 150,
            type: this.isStatusApproved() ? 'text' : 'select',
            options: this.warehouseOptions(),
            required: true,
            placeholder: this.translateService.instant(
              'purchase_request.placeholders.select_warehouse'
            ),
            filter: true,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.actions'
            ),
            prop: 'actions',
            type: 'actions',
            width: 120,
          },
        ],
        data: [],
        actions: [
          {
            label: this.translateService.instant(
              'purchase_request.actions.delete'
            ),
            action: 'deleteSelectedItems',
            severity: 'danger',
            outlined: true,
            disabled: true,
            icon: 'delete',
          },
          {
            label: this.translateService.instant(
              'purchase_request.actions.add_item'
            ),
            action: 'addItem',
            severity: 'info',
            icon: 'add',
          },
        ],
        hasCheckbox: true,
      },
    ]);

    // Initialize header actions with translated labels
    this.initializeHeaderActions();
  }

  private initializeHeaderActions(): void {
    const actions: ActionButton[] = [
      {
        label: this.translateService.instant('purchase_request.actions.cancel'),
        action: 'cancel',
        outlined: true,
        severity: 'secondary' as const,
      },
    ];

    // Show save button only if status is not approved
    if (!this.isStatusApproved()) {
      actions.unshift({
        label: this.translateService.instant('purchase_request.actions.save'),
        action: 'save',
        severity: 'info' as const,
      });
    }

    // Show purchase order buttons based on status
    if (this.isStatusPending()) {
      actions.splice(1, 0, {
        label: this.translateService.instant(
          'purchase_request.actions.create_purchase_order'
        ),
        action: 'createPurchaseOrder',
        severity: 'success' as const,
        icon: 'add',
      });
    } else if (this.isStatusApproved()) {
      // Check if purchase order exists and show appropriate button
      const hasPurchaseOrder = this.checkPurchaseOrderExists();
      if (hasPurchaseOrder) {
        actions.splice(1, 0, {
          label: this.translateService.instant(
            'purchase_request.actions.edit_purchase_order'
          ),
          action: 'editPurchaseOrder',
          severity: 'info' as const,
          icon: 'edit',
        });
      } else {
        // Show create button when status is approved but no orders exist
        actions.splice(1, 0, {
          label: this.translateService.instant(
            'purchase_request.actions.create_purchase_order'
          ),
          action: 'createPurchaseOrder',
          severity: 'success' as const,
          icon: 'add',
        });
      }
    }

    this.headerActions.set(actions);
  }

  // Item management methods
  private onAddItem(): void {
    this.editingItem.set(null);
    this.showItemDialog.set(true);
  }

  private onEditItem(data: PurchaseRequestItem): void {
    this.editingItem.set(data);
    this.showItemDialog.set(true);
  }

  private onDeleteItem(data: PurchaseRequestItem): void {
    // Only add to deleted items if it has a valid ID (existing item from API)
    if (data.id && Number(data.id) > 0) {
      const deletedItem = { ...data, deleteFlag: true };
      this.deletedItems.set([...this.deletedItems(), deletedItem]);
    }
    // For new items (no ID or ID = 0), just filter them out without adding to deleted

    // Remove from current items list
    const updatedList = this.itemsList().filter((item) => item.id !== data.id);

    // Reorder remaining items
    const reorderedItems = this.reorderItems(updatedList);
    this.itemsList.set(reorderedItems);
    this.updateItemsTableData();
  }

  private onDeleteSelectedItems(): void {
    const selectedItems = this.selectedItems();
    if (selectedItems.length === 0) return;

    // Only add items with valid IDs to deleted items
    const itemsToDelete = selectedItems.filter(
      (item) => item.id && Number(item.id) > 0
    );
    const deletedItemsWithFlag = itemsToDelete.map((item) => ({
      ...item,
      deleteFlag: true,
    }));
    this.deletedItems.set([...this.deletedItems(), ...deletedItemsWithFlag]);

    // Remove selected items from current items list
    const selectedIds = selectedItems.map((item) => item.id);
    const updatedList = this.itemsList().filter(
      (item) => !selectedIds.includes(item.id)
    );

    // Reorder remaining items
    const reorderedItems = this.reorderItems(updatedList);
    this.itemsList.set(reorderedItems);
    this.selectedItems.set([]);
    this.updateItemsTableData();
  }

  onSelectionChange(section: string | undefined, selectedItems: any[]): void {
    if (
      section ===
      this.translateService.instant('purchase_request.sections.item_details')
    ) {
      this.selectedItems.set(selectedItems);
      this.updateItemsTableData();
    }
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    // Emit the change to the debounced subject instead of updating immediately
    // This prevents the input field from losing focus on every keystroke
    this.cellValueChange$.next(event);
  }

  private updateItemsListFromCellChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    // Update the itemsList signal with the new value (debounced)
    const updatedItemsList = this.itemsList().map((item) => {
      if (item.id === event.rowData.id) {
        let updatedValue = event.value;

        // Handle special cases for different field types
        if (event.prop === 'quantity') {
          // Ensure quantity is a number
          updatedValue = event.value || 0;
        }

        const updatedItem = { ...item, [event.prop]: updatedValue };
        return updatedItem;
      }
      return item;
    });

    this.itemsList.set(updatedItemsList);

    // Update table data to refresh validation errors
    this.updateItemsTableData();
  }

  onItemDialogConfirm(item: PurchaseRequestItem): void {
    if (this.editingItem()) {
      // Update existing item
      const updatedList = this.itemsList().map((existingItem) =>
        existingItem.id === item.id ? item : existingItem
      );
      this.itemsList.set(updatedList);
    } else {
      // Add new item - check for duplicates by SKU code
      const existingItems = this.itemsList();
      const isDuplicate = existingItems.some(
        (existingItem) => existingItem.skuCode === item.skuCode
      );

      if (!isDuplicate) {
        const newList = [...existingItems, item];
        this.itemsList.set(newList);
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant(
            'purchase_request.messages.sku_code_already_exists'
          ),
        });
      }
    }
    this.updateItemsTableData();
  }

  private loadWarehouses(): void {
    this.warehouseOptionsService
      .getOptionsPage({ page: 1, size: 50, searchText: '' }, { valueKey: 'code' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.warehouseOptions.set(
            page.options.map((o) => ({
              label: String(o.label),
              value: String(o.value),
            })),
          );
          this.updateWarehouseColumnOptions();
        },
        error: (error) => {
          console.error('Failed to load warehouses:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_load_warehouses',
            ),
          });
        },
      });
  }

  private updateWarehouseColumnOptions(): void {
    const currentSections = this.tableSections();
    const warehouseOptions = [...this.warehouseOptions()]; // Create new array reference
    const updatedSections = currentSections.map((section) => {
      if (!section.columns) {
        return section;
      }
      const updatedColumns = section.columns.map((col) => {
        if (col.prop === 'warehouseCode') {
          // Create a completely new column object to trigger change detection
          return {
            ...col,
            options: warehouseOptions, // Use new array reference
          };
        }
        return col;
      });
      // Create a completely new section object to trigger change detection
      return {
        ...section,
        columns: [...updatedColumns],
      };
    });
    this.tableSections.set([...updatedSections]);
  }

  private updateItemsTableData(): void {
    const currentSections = this.tableSections();
    const warehouseOptions = [...this.warehouseOptions()]; // Create new array reference
    const updatedSections = currentSections.map((section) => {
      // Check if this is the item details section by looking for the SPU code column
      const hasSpuCodeColumn = section.columns?.some(
        (col) => col.prop === 'spuCode'
      );
      if (hasSpuCodeColumn && section.columns) {
        const updatedColumns = section.columns.map((col) => {
          if (col.prop === 'warehouseCode') {
            // Create a completely new column object to trigger change detection
            return {
              ...col,
              options: warehouseOptions, // Use new array reference
            };
          }
          return col;
        });
        return {
          ...section,
          columns: [...updatedColumns],
          data: [...this.itemsList()], // Create new array reference
          selection: [...this.selectedItems()], // Create new array reference
          actions: section.actions?.map((action) => {
            if (action.action === 'deleteSelectedItems') {
              return {
                ...action,
                disabled: this.selectedItems().length === 0,
              };
            }
            return action;
          }),
        };
      }
      return section;
    });
    this.tableSections.set([...updatedSections]);
  }

  /**
   * Validate required item data and duplicate SKU + warehouse combinations
   * Returns array of error messages
   */
  private validateItems(): string[] {
    const errors: string[] = [];
    const items = this.itemsList();
    const skuWarehouseMap = new Map<string, number[]>();

    items.forEach((item, index) => {
      if (!item.warehouseCode) {
        errors.push('missing_warehouse');
      }

      if (item.skuCode && item.warehouseCode) {
        const key = `${item.skuCode}_${item.warehouseCode}`;
        if (!skuWarehouseMap.has(key)) {
          skuWarehouseMap.set(key, []);
        }
        skuWarehouseMap.get(key)!.push(index);
      }
    });

    // Check for duplicates
    skuWarehouseMap.forEach((indices, key) => {
      if (indices.length > 1) {
        errors.push(key);
      }
    });

    return errors;
  }

  private hasMissingRequiredFormData(): boolean {
    return !this.currentFormData?.series
      || !this.currentFormData?.title
      || !this.currentFormData?.scheduleDate;
  }

  /**
   * Get field error for table cell validation
   * Used by add-form component to display validation errors
   */
  getFieldError = (rowIndex: number, fieldName: string): string | null => {
    if (fieldName !== 'warehouseCode') {
      return null;
    }

    const items = this.itemsList();
    if (rowIndex < 0 || rowIndex >= items.length) {
      return null;
    }

    const currentItem = items[rowIndex];
    if (!currentItem.warehouseCode && this.showRequiredValidationErrors()) {
      return this.translateService.instant(
        'purchase_request.messages.please_fill_required_fields'
      );
    }

    if (!currentItem.skuCode) {
      return null;
    }

    // Check if this SKU + warehouse combination exists in other rows
    const duplicateCount = items.filter(
      (item, index) =>
        index !== rowIndex &&
        item.skuCode === currentItem.skuCode &&
        item.warehouseCode === currentItem.warehouseCode &&
        item.warehouseCode // Ensure warehouse code is not empty
    ).length;

    if (duplicateCount > 0) {
      // Translate the error message before returning
      return this.translateService.instant(
        'purchase_request.validation.duplicate_sku_warehouse'
      );
    }

    return null;
  };
}
